import { PromotionContext } from "@database/contexts"
import { useCallback, useContext, useState } from "react"
import { useOrganisation } from "./useOrganisation";
import { ErrorType, Promotion } from "@utilities/types";
import { CampaignsResponse, ProductWithPromotion, promotionService } from "@database/services";

export const usePromotion = () => {

    const {
        allPromotions, setAllPromotions,
        selectedPromotion, setSelectedPromotion,
    } = useContext(PromotionContext);
    const { organisation } = useOrganisation();

    const [getAllOrganisationPromotionError, setGetAllOrganisationPromotionError] = useState<ErrorType | null>(null);
    const [getAllOrganisationPromotionLoading, setGetAllOrganisationPromotionLoading] = useState<boolean>(false);
    const [getOnePromotionError, setGetOnePromotionError] = useState<ErrorType | null>(null);
    const [getOnePromotionLoading, setGetOnePromotionLoading] = useState<boolean>(false);
    const [createPromotionError, setCreatePromotionError] = useState<ErrorType | null>(null);
    const [createPromotionLoading, setCreatePromotionLoading] = useState<boolean>(false);
    const [updatePromotionError, setUpdatePromotionError] = useState<ErrorType | null>(null);
    const [updatePromotionLoading, setUpdatePromotionLoading] = useState<boolean>(false);
    const [addPromotionToCategoryError, setAddPromotionToCategoryError] = useState<ErrorType | null>(null);
    const [addPromotionToCategoryLoading, setAddPromotionToCategoryLoading] = useState<boolean>(false);
    const [addPromotionToProductError, setAddPromotionToProductError] = useState<ErrorType | null>(null);
    const [addPromotionToProductLoading, setAddPromotionToProductLoading] = useState<boolean>(false);
    const [deletePromotionError, setDeletePromotionError] = useState<ErrorType | null>(null);
    const [deletePromotionLoading, setDeletePromotionLoading] = useState<boolean>(false);
    const [deletePromotionFromCategoryError, setDeletePromotionFromCategoryError] = useState<ErrorType | null>(null);
    const [deletePromotionFromCategoryLoading, setDeletePromotionFromCategoryLoading] = useState<boolean>(false);
    const [deletePromotionFromProductError, setDeletePromotionFromProductError] = useState<ErrorType | null>(null);
    const [deletePromotionFromProductLoading, setDeletePromotionFromProductLoading] = useState<boolean>(false);

    // Nouveaux états pour les campagnes
    const [campaigns, setCampaigns] = useState<CampaignsResponse | null>(null);
    const [campaignsLoading, setCampaignsLoading] = useState<boolean>(false);
    const [campaignsError, setCampaignsError] = useState<ErrorType | null>(null);
    const [productsWithPromo, setProductsWithPromo] = useState<ProductWithPromotion[]>([]);
    const [productsWithPromoLoading, setProductsWithPromoLoading] = useState<boolean>(false);
    const [productsWithPromoError, setProductsWithPromoError] = useState<ErrorType | null>(null);

    const onGetAllOrganisationPromotion = useCallback(async () => {
        if (!organisation) return;
        setGetAllOrganisationPromotionLoading(true);

        const req = await promotionService.getAllOrganisationPromotions(organisation._id);
        if (req.code === 200) {
            setAllPromotions(req.data);
            setGetAllOrganisationPromotionError(null);
        } else {
            setGetAllOrganisationPromotionError({
                message: (req.code === 400 || req.code === 404) ?
                    "Pas de promotions trouvées, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de la récupération des promotions",
            });
        }
        setGetAllOrganisationPromotionLoading(false);
    }, [organisation, setAllPromotions]);

    const onGetOnePromotion = useCallback(async (promoCode: string) => {
        if (!organisation) return;
        setGetOnePromotionLoading(true);
        const req = await promotionService.getOnePromotion(organisation._id, promoCode);
        if (req.code === 200) {
            setSelectedPromotion(req.data);
            setGetOnePromotionError(null);
        } else {
            setGetOnePromotionError({
                message: (req.code === 400 || req.code === 404) ?
                    "Code invalide, verrifiez le code envoyé" :
                    "Une erreur est survenue lors de la récupération de la promotion",
            });
        }
        setGetOnePromotionLoading(false);
    }, [organisation, setSelectedPromotion]);

    const onCreatePromotion = useCallback(async (promotionData: Partial<Promotion>) => {
        if (!organisation) return;

        setCreatePromotionLoading(true);
        const req = await promotionService.createPromotion({
            ...promotionData,
            organisationID: organisation._id,
        });
        if (req.code === 201) {
            await onGetAllOrganisationPromotion();
            setCreatePromotionError(null);
        } else {
            setCreatePromotionError({
                message: req.code === 401 ?
                    "Erreur lors de la création de la promotion, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de la création de la promotion",
            });
        }
        setCreatePromotionLoading(false);
    }, [organisation, onGetAllOrganisationPromotion]);

    const onUpdatePromotion = useCallback(async (promotionID: string, promotionData: Partial<Promotion>) => {
        setUpdatePromotionLoading(true);
        const req = await promotionService.updatePromotion(promotionID, promotionData);
        if (req.code === 200) {
            await onGetAllOrganisationPromotion();
            setUpdatePromotionError(null);
        } else {
            setUpdatePromotionError({
                message: (req.code === 400 || req.code === 404) ?
                    "Erreur lors de la mise à jour de la promotion, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de la mise à jour de la promotion",
            });
        }
        setUpdatePromotionLoading(false);
    }, [onGetAllOrganisationPromotion]);

    const onAddPromotionToCategory = useCallback(async (categoryID: string, promotionID: string) => {
        setAddPromotionToCategoryLoading(true);
        const req = await promotionService.addPromotionToCategory(categoryID, promotionID);
        if (req.code === 200) {
            await onGetAllOrganisationPromotion();
            setAddPromotionToCategoryError(null);
        } else {
            setAddPromotionToCategoryError({
                message: (req.code === 400 || req.code === 404) ?
                    "Erreur lors de l'ajout de la promotion à la catégorie, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de l'ajout de la promotion à la catégorie",
            });
        }
        setAddPromotionToCategoryLoading(false);
    }, [onGetAllOrganisationPromotion]);

    const onAddPromotionToProduct = useCallback(async (productID: string, promotionID: string) => {
        setAddPromotionToProductLoading(true);
        const req = await promotionService.addPromotionToProduct(productID, promotionID);
        if (req.code === 200) {
            await onGetAllOrganisationPromotion();
            setAddPromotionToProductError(null);
        } else {
            setAddPromotionToProductError({
                message: (req.code === 400 || req.code === 404) ?
                    "Erreur lors de l'ajout de la promotion au produit, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de l'ajout de la promotion au produit",
            });
        }
        setAddPromotionToProductLoading(false);
    }, [onGetAllOrganisationPromotion]);

    const onDeletePromotion = useCallback(async (promotionID: string) => {
        setDeletePromotionLoading(true);
        const req = await promotionService.deletePromotion(promotionID);
        if (req.code === 200) {
            await onGetAllOrganisationPromotion();
            setDeletePromotionError(null);
        } else {
            setDeletePromotionError({
                message: (req.code === 400 || req.code === 404) ?
                    "Erreur lors de la suppression de la promotion, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de la suppression de la promotion",
            });
        }
        setDeletePromotionLoading(false);
    }, [onGetAllOrganisationPromotion]);

    const onDeletePromotionFromCategory = useCallback(async (categoryID: string) => {
        setDeletePromotionFromCategoryLoading(true);
        const req = await promotionService.deletePromotionFromCategory(categoryID);
        if (req.code === 200) {
            await onGetAllOrganisationPromotion();
            setDeletePromotionFromCategoryError(null);
        } else {
            setDeletePromotionFromCategoryError({
                message: (req.code === 400 || req.code === 404) ?
                    "Erreur lors de la suppression de la promotion de la catégorie, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de la suppression de la promotion de la catégorie",
            });
        }
        setDeletePromotionFromCategoryLoading(false);
    }, [onGetAllOrganisationPromotion]);

    const onDeletePromotionFromProduct = useCallback(async (productID: string) => {
        setDeletePromotionFromProductLoading(true);
        const req = await promotionService.deletePromotionFromProduct(productID);
        if (req.code === 200) {
            await onGetAllOrganisationPromotion();
            setDeletePromotionFromProductError(null);
        } else {
            setDeletePromotionFromProductError({
                message: (req.code === 400 || req.code === 404) ?
                    "Erreur lors de la suppression de la promotion du produit, verrifiez les données envoyées" :
                    "Une erreur est survenue lors de la suppression de la promotion du produit",
            });
        }
        setDeletePromotionFromProductLoading(false);
    }, [onGetAllOrganisationPromotion]);

    // Nouvelles fonctions pour les campagnes
    const onGetAllCampaigns = useCallback(async () => {
        if (!organisation) return;
        setCampaignsLoading(true);
        try {
            const req = await promotionService.getAllCampaigns(organisation._id);
            if (req) {
                setCampaigns(req);
                setCampaignsError(null);
            }
        } catch (error) {
            setCampaignsError({
                message: "Une erreur est survenue lors de la récupération des campagnes",
            });
        }
        setCampaignsLoading(false);
    }, [organisation]);

    const onGetProductsWithPromotion = useCallback(async (page?: number, limit?: number, categoryID?: string) => {
        if (!organisation) return;
        setProductsWithPromoLoading(true);
        try {
            const req = await promotionService.getProductsWithPromotion(organisation._id, page, limit, categoryID);
            if (req.code === 200) {
                setProductsWithPromo(req.data.data);
                setProductsWithPromoError(null);
                return req.data;
            } else {
                setProductsWithPromoError({
                    message: "Erreur lors de la récupération des produits avec promotion",
                });
            }
        } catch (error) {
            setProductsWithPromoError({
                message: "Une erreur est survenue lors de la récupération des produits",
            });
        }
        setProductsWithPromoLoading(false);
    }, [organisation]);

    return {
        allPromotions, getAllOrganisationPromotionError,
        getAllOrganisationPromotionLoading,
        createPromotionError, createPromotionLoading,
        updatePromotionError, updatePromotionLoading,
        addPromotionToCategoryError, addPromotionToCategoryLoading,
        addPromotionToProductError, addPromotionToProductLoading,
        deletePromotionError, deletePromotionLoading,
        deletePromotionFromCategoryError, deletePromotionFromCategoryLoading,
        deletePromotionFromProductError, deletePromotionFromProductLoading,
        selectedPromotion, getOnePromotionError, getOnePromotionLoading,
        setGetOnePromotionError,
        // Nouvelles propriétés pour les campagnes
        campaigns, campaignsLoading, campaignsError,
        productsWithPromo, productsWithPromoLoading, productsWithPromoError,
        onGetAllOrganisationPromotion, onCreatePromotion,
        onUpdatePromotion, onAddPromotionToCategory, setSelectedPromotion,
        onAddPromotionToProduct, onDeletePromotion, onGetOnePromotion,
        onDeletePromotionFromCategory, onDeletePromotionFromProduct,
        // Nouvelles fonctions pour les campagnes
        onGetAllCampaigns, onGetProductsWithPromotion,
    }
}
